// src/pages/BlockedUsers.tsx
// Plezyr Admin - Blocked Users Management
import React, { useState } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Box,
  Avatar,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useTranslation } from 'react-i18next';

interface BlockedUser {
  id: string;
  userId: string;
  userName: string;
  email: string;
  avatar: string;
  blockedAt: string;
  reason: string;
  blockedBy: string;
  blockType: 'temporary' | 'permanent';
  expiresAt?: string;
}

const BlockedUsers: React.FC = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [unblockDialog, setUnblockDialog] = useState<BlockedUser | null>(null);

  // Placeholder data - will be replaced with API call
  const [blockedUsers] = useState<BlockedUser[]>([
    {
      id: '1',
      userId: 'user_002',
      userName: 'Lucas Martin',
      email: 'lucas@example.com',
      avatar: '',
      blockedAt: '2024-01-15',
      reason: 'Fake profile - using stolen photos',
      blockedBy: 'Admin',
      blockType: 'permanent',
    },
    {
      id: '2',
      userId: 'user_004',
      userName: 'Pierre Durand',
      email: 'pierre@example.com',
      avatar: '',
      blockedAt: '2024-01-14',
      reason: 'Harassment of other users',
      blockedBy: 'Admin',
      blockType: 'temporary',
      expiresAt: '2024-02-14',
    },
    {
      id: '3',
      userId: 'user_007',
      userName: 'Marc Robert',
      email: 'marc@example.com',
      avatar: '',
      blockedAt: '2024-01-10',
      reason: 'Spam and promotional content',
      blockedBy: 'System',
      blockType: 'permanent',
    },
  ]);

  const blockTypeChip = (type: BlockedUser['blockType']) => {
    if (type === 'permanent') {
      return <Chip label={t('blocked.permanent', 'Permanent')} color="error" size="small" />;
    }
    return <Chip label={t('blocked.temporary', 'Temporaire')} color="warning" size="small" />;
  };

  const filteredUsers = blockedUsers.filter(
    (u) =>
      u.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.reason.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleUnblock = (user: BlockedUser) => {
    setUnblockDialog(user);
  };

  const confirmUnblock = () => {
    // TODO: Implement API call to unblock user
    console.log('Unblocking user:', unblockDialog?.userId);
    setUnblockDialog(null);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: '#1e293b' }}>
        {t('blocked.title', 'Utilisateurs bloqués')}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {t('blocked.subtitle', 'Gérez les comptes bloqués ou bannis de l\'application')}
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <TextField
          placeholder={t('blocked.search', 'Rechercher un utilisateur bloqué...')}
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ width: 350 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
        <Chip
          label={`${filteredUsers.length} ${t('blocked.count', 'utilisateur(s) bloqué(s)')}`}
          color="error"
          variant="outlined"
        />
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8fafc' }}>
              <TableCell sx={{ fontWeight: 600 }}>{t('blocked.user', 'Utilisateur')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('blocked.email', 'Email')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('blocked.reason', 'Raison')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('blocked.type', 'Type')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('blocked.blockedAt', 'Bloqué le')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('blocked.blockedBy', 'Par')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">{t('blocked.actions', 'Actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map((user) => (
              <TableRow key={user.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: '#ef4444' }}>
                      {user.userName.charAt(0)}
                    </Avatar>
                    <Typography variant="body2" fontWeight={500}>
                      {user.userName}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {user.reason}
                  </Typography>
                </TableCell>
                <TableCell>
                  {blockTypeChip(user.blockType)}
                  {user.expiresAt && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      {t('blocked.until', 'Jusqu\'au')} {user.expiresAt}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{user.blockedAt}</TableCell>
                <TableCell>{user.blockedBy}</TableCell>
                <TableCell align="center">
                  <Tooltip title={t('blocked.viewProfile', 'Voir le profil')}>
                    <IconButton size="small" color="primary">
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('blocked.unblock', 'Débloquer')}>
                    <IconButton size="small" color="success" onClick={() => handleUnblock(user)}>
                      <LockOpenIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('blocked.deleteAccount', 'Supprimer le compte')}>
                    <IconButton size="small" color="error">
                      <DeleteForeverIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredUsers.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography color="text.secondary">
            {t('blocked.noResults', 'Aucun utilisateur bloqué')}
          </Typography>
        </Box>
      )}

      {/* Unblock Confirmation Dialog */}
      <Dialog open={!!unblockDialog} onClose={() => setUnblockDialog(null)}>
        <DialogTitle>{t('blocked.confirmUnblock', 'Confirmer le déblocage')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('blocked.confirmUnblockMessage', 'Êtes-vous sûr de vouloir débloquer')} <strong>{unblockDialog?.userName}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('blocked.originalReason', 'Raison du blocage')}: {unblockDialog?.reason}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnblockDialog(null)}>{t('common.cancel', 'Annuler')}</Button>
          <Button onClick={confirmUnblock} variant="contained" color="success">
            {t('blocked.unblock', 'Débloquer')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BlockedUsers;
